// Sichtweite und Nebel: Welche Chunks werden überhaupt gezeichnet?
//
// Die Welt aus Paket 2 ist 16 × 16 Chunks groß, also sechzehnmal so viel wie das Dorf der Demo.
// Alle 256 Chunk-Meshes je Bild an die Grafik zu geben, kostet Zeichenaufrufe, die nichts
// einbringen: Was hinter dem Nebel liegt, sieht man ohnehin nicht. Darum bekommt jeder Chunk je
// Bild ein `visible` aus dem waagerechten Abstand zur Kamera, und der Nebel wird genau vor dieser
// Grenze dicht, damit die Kante nicht auffällt (ADR 0025).
//
// Reine Rechnung ohne Three.js, damit sie geprüft ist; `scene.ts` wendet sie an.

import { CHUNK_SIZE } from '@bauernhof/shared';

/**
 * Sichtweite in Blöcken: sieben Chunks. Damit reicht der Blick vom Dorfplatz über die erste
 * Parzellenreihe hinaus, und höchstens rund ein Viertel der Welt wird gezeichnet.
 */
export const SICHTWEITE = 7 * CHUNK_SIZE;

/** Anteil der Sichtweite, ab dem der Nebel einsetzt. */
export const NEBEL_ANTEIL = 0.55;

/** Ein Punkt auf der Grundfläche; die Höhe spielt für die Sichtweite keine Rolle. */
export interface Grundriss {
  x: number;
  z: number;
}

/**
 * Waagerechter Abstand von `kamera` zum nächsten Punkt des Chunks (cx, cz), in Blöcken. Steht die
 * Kamera im Chunk, ist er 0. Gemessen wird zur Kante, nicht zur Mitte: Sonst verschwände ein
 * Chunk, in dessen naher Ecke man noch steht.
 */
export function chunkAbstand(cx: number, cz: number, kamera: Grundriss): number {
  const dx = achsenAbstand(kamera.x, cx * CHUNK_SIZE);
  const dz = achsenAbstand(kamera.z, cz * CHUNK_SIZE);
  return Math.hypot(dx, dz);
}

/** Abstand eines Wertes zum Intervall [von, von + CHUNK_SIZE]; innerhalb 0. */
function achsenAbstand(wert: number, von: number): number {
  return Math.max(von - wert, wert - (von + CHUNK_SIZE), 0);
}

/** Ob der Chunk (cx, cz) noch innerhalb der Sichtweite liegt und darum gezeichnet wird. */
export function chunkImBlick(cx: number, cz: number, kamera: Grundriss, sichtweite: number): boolean {
  return chunkAbstand(cx, cz, kamera) <= sichtweite;
}

/** Anfang und Ende des Nebels zu einer Sichtweite. */
export interface NebelBereich {
  nah: number;
  fern: number;
}

/**
 * Der Nebel endet genau auf der Sichtweite: Was dahinter wegfällt, ist ohnehin in Himmelfarbe
 * getaucht. Er beginnt bei `NEBEL_ANTEIL` der Sichtweite, damit der eigene Hof klar bleibt.
 */
export function nebelBereich(sichtweite: number): NebelBereich {
  return { nah: sichtweite * NEBEL_ANTEIL, fern: sichtweite };
}
